import React, { useState } from 'react';
import { PercentInput } from '@app/react-common/components/percent-input/PercentInput';
import { ToggleButton } from '@app/react-common/components/toggle-button/ToggleButton';
import { Checkbox } from '@app/react-common/components/checkbox/Checkbox';
import { DisplayOptimisationSettings } from '../../../../models/CheckoutGroupSettings';
import { CheckoutSettings } from '@pdqdev/pdq-shared/dist/entities';
import Tooltip from '@app/react-common/components/tooltip/Tooltip';

interface DisplayOptimisationSettingsProps {
  checkoutSettings?: CheckoutSettings;
  displayOptimisationSettings?: DisplayOptimisationSettings;
  onChange?: (displayOptimisationSettings: DisplayOptimisationSettings) => void;
}

export const DisplayOptimisationSettingsView: React.FC<DisplayOptimisationSettingsProps> = (
  props: DisplayOptimisationSettingsProps,
) => {
  const [state, setState] = useState<DisplayOptimisationSettings>(props?.displayOptimisationSettings ?? null);

  const handleToggleChange = (value: boolean) => {
    setState({ ...state, enableDisplayRatesOptimization: value });
    props.onChange({ ...state, enableDisplayRatesOptimization: value });
  };

  const handleCheckboxChange = (checked: boolean) => {
    setState({ ...state, enableIncreaseRatesPercentage: checked });
    props.onChange({ ...state, enableIncreaseRatesPercentage: checked });
  };

  // Deliberately not gated on enableDisplayRatesOptimization: the rule runs in the checkout function.
  const handleSameNameToggleChange = (value: boolean) => {
    setState({ ...state, enableSameNameShippingMethodsKeepCheapestOnly: value });
    props.onChange({ ...state, enableSameNameShippingMethodsKeepCheapestOnly: value });
  };

  const handlePercentageValue = (value: number) => {
    setState({ ...state, increaseRatesPercentage: value });
    props.onChange({ ...state, increaseRatesPercentage: value });
  };

  return (
    <div className="display-optmisation-settings">
      <div className="display-optmisation-settings__header-row">
        <h3 className="display-optmisation-settings__sub-title">Display Optimization</h3>
        <ToggleButton checked={state?.enableDisplayRatesOptimization} onChange={handleToggleChange} />
      </div>
      <div className="display-optmisation-settings__label">Only show the cheapest and fastest methods</div>
      <div>
        <div className="display-optmisation-settings__label-secondary">
          If two methods have the same price, only display the fastest
        </div>
        <div className="display-optmisation-settings__label-secondary">
          If two methods have the same delivery date, only display the cheapest
        </div>
      </div>
      <div className="display-optmisation-settings__same-name-row">
        <ToggleButton
          checked={state?.enableSameNameShippingMethodsKeepCheapestOnly}
          onChange={handleSameNameToggleChange}
          ariaLabel="If two methods have the same name, only display the cheapest"
        />
        <span className="display-optmisation-settings__label-secondary">
          If two methods have the same name, only display the cheapest
        </span>
        {!props.checkoutSettings?.enableDeliveryCustomization && (
          <Tooltip
            tooltipText={
              'To hide same-name methods, please enable Delivery Customization in Checkout Settings. This rule also hides your own Shopify rates that share a name with a PDQ method.'
            }>
            <i className="icon icon-more_information" />
          </Tooltip>
        )}
      </div>
      <Checkbox
        label="Then optimize your fastest method"
        checked={state?.enableIncreaseRatesPercentage}
        onChange={handleCheckboxChange}
      />

      <div className="display-optmisation-settings__label">
        Don't display methods that cost more than x% of total cart value
        <Tooltip tooltipText={'Cart value can mean subtotal or total of cart, as chosen in your checkout settings'}>
          <i className="icon icon-more_information" />
        </Tooltip>
      </div>
      <div className="display-optmisation-settings__label-secondary display-optmisation-settings__label-indented-left">
        {' '}
        If there is no methods to show: this rule won't be applied
      </div>
      <div className="display-optmisation-settings__label-round-up">
        {'\u2022 Round up to x% of order value of the fastest delivery method'}
      </div>
      <div className="display-optmisation-settings__percentage-container">
        <div className={`display-optmisation-settings__percentage-container__title`}> x% = </div>
        <div className="display-optmisation-settings__percentage-container__percentage">
          <PercentInput
            value={state?.increaseRatesPercentage}
            setValue={handlePercentageValue}
            disabled={!state?.enableIncreaseRatesPercentage}
            setValid={() => {}}
            viewMode={false}
          />
        </div>
      </div>
    </div>
  );
};
